import React, { useState, useEffect, useMemo } from "react";
import Sidebar from "../../component/Sidebar";
import Topbar from "../../component/Topbar";
import { FaBoxOpen, FaFilter, FaChevronDown, FaChevronUp, FaChevronLeft, FaChevronRight } from "react-icons/fa";
import axios from "axios";
import { baseurl, imgurl } from "../../baseurl";

function StockReport() {
  const [showFilter, setShowFilter] = useState(false);
  const [filters, setFilters] = useState({ search: "" });
  const [stockReport, setStockReport] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});

  // 🔢 Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const vendorId = localStorage.getItem("vendorId");

  useEffect(() => {
    const fetchStockReport = async () => {
      try {
        setLoading(true);
        const res = await axios.get(`${baseurl}getVendorStockReport/${vendorId}`);
        if (res.data.success) {
          setStockReport(res.data.stock_report || []);
        }
      } catch (err) {
        console.error("Error fetching vendor stock report:", err);
      } finally {
        setLoading(false);
      }
    };
    if (vendorId) fetchStockReport();
  }, [vendorId]);

  const toggleExpand = (uuid) => {
    setExpanded((prev) => ({ ...prev, [uuid]: !prev[uuid] }));
  };

  // 🔍 Filter Logic
  const filteredProducts = useMemo(() => {
    setCurrentPage(1); // Reset to page 1 when searching
    return stockReport.filter((p) =>
      `${p.product_name} ${p.sku}`.toLowerCase().includes(filters.search.toLowerCase())
    );
  }, [stockReport, filters.search]);

  // 📉 Pagination Logic Calculation
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredProducts.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);

  // 🛠️ Generate Page Numbers with "..." Logic
  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 7;

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      // Always show first page
      pages.push(1);

      if (currentPage > 4) pages.push("...");

      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);

      for (let i = start; i <= end; i++) {
        if (!pages.includes(i)) pages.push(i);
      }

      if (currentPage < totalPages - 3) pages.push("...");

      // Always show last page
      if (!pages.includes(totalPages)) pages.push(totalPages);
    }
    return pages;
  };

  return (
    <>
      <Topbar />
      <Sidebar />

      <main className="main-wrapper">
        <div className="main-content">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h4 className="breadcrumb-title d-flex align-items-center gap-2">
              <FaBoxOpen /> Stock Report
            </h4>
            <button className="btn btn-primary d-flex align-items-center gap-2" onClick={() => setShowFilter(!showFilter)}>
              <FaFilter /> Filters
            </button>
          </div>

          {showFilter && (
            <div className="card p-3 mb-3 shadow-sm border-0">
              <div className="row g-2 align-items-end">
                <div className="col-md-6">
                  <label className="small fw-bold">Search Product</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Product name or SKU"
                    value={filters.search}
                    onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  />
                </div>
                <div className="col-md-2">
                  <button className="btn btn-primary w-100" onClick={() => setShowFilter(false)}>Apply</button>
                </div>
              </div>
            </div>
          )}

          <div className="card shadow-sm border-0">
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-hover align-middle mb-0">
                  <thead >
                    <tr>
                      <th className="ps-3">#</th>
                      <th>Image</th>
                      <th>Product</th>
                      <th>SKU</th>
                      <th>Price</th>
                      <th>MRP</th>
                      <th>Stock</th>
                      <th>Sold</th>
                      <th>Reserved</th>
                      <th className="pe-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr><td colSpan="10" className="text-center py-5">Loading...</td></tr>
                    ) : currentItems.length > 0 ? (
                      currentItems.map((p, i) => (
                        <React.Fragment key={p.product_uuid}>
                          <tr>
                            <td className="ps-3 text-muted">{indexOfFirstItem + i + 1}</td>
                            <td>
                              <img src={p.featured_image ? `${imgurl}${p.featured_image}` : "/noimage.png"} 
                                   alt="" style={{ width: "40px", height: "40px", borderRadius: "6px", objectFit: "cover" }} />
                            </td>
                            <td className="fw-semibold">{p.product_name}</td>
                            <td className="text-muted small">{p.sku}</td>
                            <td>₹{p.price}</td>
                            <td className="text-muted small text-decoration-line-through">₹{p.mrp}</td>
                            <td className={`fw-bold ${p.total_stock < 5 ? 'text-danger' : ''}`}>{p.total_stock}</td>
                            <td>{p.sold}</td>
                            <td>{p.reserved}</td>
                            <td className="pe-3">
                              {p.variants?.length > 0 && (
                                <button className="btn btn-sm btn-light border" onClick={() => toggleExpand(p.product_uuid)}>
                                  {expanded[p.product_uuid] ? <FaChevronUp /> : <FaChevronDown />}
                                </button>
                              )}
                            </td>
                          </tr>
                          {/* Variants mapping remains the same as your previous logic */}
                          {expanded[p.product_uuid] && p.variants?.length > 0 && (
                            <tr>
                                <td colSpan="10" className="bg-light p-0">
                                    <div className="p-3 border-top mx-3 my-2 bg-white rounded shadow-sm">
                                        <h6 className="small fw-bold text-primary mb-2">Variant Breakdown</h6>
                                        {/* Your existing variants table/rows go here */}
                                        <div className="row small fw-bold text-muted border-bottom pb-1">
                                            <div className="col-4">Variant Name / SKU</div>
                                            <div className="col-2">Price</div>
                                            <div className="col-2">Stock</div>
                                            <div className="col-4">Images</div>
                                        </div>
                                        {p.variants.map(v => (
                                            <div key={v.variant_id} className="row py-2 border-bottom align-items-center small">
                                                <div className="col-4">
                                                    <div>{v.variant_json.name}</div>
                                                    <div className="extra-small text-muted">{v.variant_json.sku}</div>
                                                </div>
                                                <div className="col-2">₹{v.variant_json.price}</div>
                                                <div className="col-2 fw-bold">{v.stock_after_change}</div>
                                                <div className="col-4 d-flex gap-1">
                                                    {v.variant_json.images?.slice(0, 3).map((img, idx) => (
                                                        <img key={idx} src={`${imgurl}${img.path}`} style={{width:'25px', height:'25px', borderRadius:'3px'}} />
                                                    ))}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))
                    ) : (
                      <tr><td colSpan="10" className="text-center py-4">No records found</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* 🛠️ Pagination Footer */}
            {!loading && filteredProducts.length > 0 && (
              <div className="card-footer bg-white border-top py-3">
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-center gap-3">
                  <div className="d-flex align-items-center gap-2">
                    <span className="small text-muted">Show</span>
                    <select 
                      className="form-select form-select-sm w-auto" 
                      value={itemsPerPage} 
                      onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                    >
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                    </select>
                    <span className="small text-muted">of {filteredProducts.length} entries</span>
                  </div>

                  <nav>
                    <ul className="pagination pagination-sm mb-0">
                      <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                        <button className="page-link" onClick={() => setCurrentPage(prev => prev - 1)}><FaChevronLeft /></button>
                      </li>
                      
                      {getPageNumbers().map((num, idx) => (
                        <li key={idx} className={`page-item ${num === currentPage ? "active" : ""} ${num === "..." ? "disabled" : ""}`}>
                          <button className="page-link" onClick={() => typeof num === 'number' && setCurrentPage(num)}>
                            {num}
                          </button>
                        </li>
                      ))}

                      <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                        <button className="page-link" onClick={() => setCurrentPage(prev => prev + 1)}><FaChevronRight /></button>
                      </li>
                    </ul>
                  </nav>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      <style jsx>{`
        .extra-small { font-size: 0.7rem; }
        .pagination .page-link { color: #333; border: none; margin: 0 2px; border-radius: 4px; }
        .pagination .page-item.active .page-link { background-color: #0d6efd; color: white; }
        .page-link:focus { box-shadow: none; }
      `}</style>
    </>
  );
}

export default StockReport;